import { Box, FileUpload, HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuUpload } from "react-icons/lu";
import { useParams } from "react-router-dom";

import { downloadDeals, uploadDeals } from "../../api/workspace";
import { Button } from "../../components/button";
import { toaster } from "../../components/toaster";
import { useLoading } from "../../hooks";

const COLUMNS = [
  { name: "name", required: true },
  { name: "stage", required: true },
  { name: "amount", required: false },
  { name: "pipeline", required: false },
  { name: "close-date", required: false },
  { name: "company-domain", required: false },
  { name: "contact-email", required: false },
];

export default function DealsPage() {
  const { workspace } = useParams();
  const [isUploading, wrapUploading] = useLoading(false);

  const download = React.useCallback(async () => {
    const csv = await downloadDeals(workspace);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "deals.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [workspace]);

  const upload = React.useCallback(
    async ({ files }: { files: File[] }) => {
      if (files.length !== 1) return;
      await wrapUploading(async () => {
        const result = await uploadDeals(workspace, files[0]);
        if (result.errors.length > 0) {
          toaster.create({
            type: "warning",
            title: `Imported ${result.imported} deals`,
            description: `${result.errors.length} error(s): ${result.errors.slice(0, 3).join("; ")}`,
          });
        } else {
          toaster.create({
            type: "success",
            title: `Imported ${result.imported} deals`,
          });
        }
      });
    },
    [workspace, wrapUploading],
  );

  return (
    <Box
      background="white"
      borderRadius="l3"
      boxShadow="card"
      maxWidth={"920px" as any}
      overflow="hidden"
      width="full"
    >
      <HStack
        borderBottomWidth="1"
        borderColor="border.soft"
        paddingX="6"
        paddingY="4"
      >
        <Stack gap="0.5">
          <Text color="fg.ink" fontSize="md" fontWeight="medium">
            Deals
          </Text>
          <Text color="fg.muted" fontSize="sm">
            Import and export deal data via CSV.
          </Text>
        </Stack>
      </HStack>
      <Stack gap="4" padding="6">
        <HStack gap="1.5" wrap="wrap">
          {COLUMNS.map(({ name, required }) => (
            <ColumnChip key={name}>
              {name}
              {required ? "*" : ""}
            </ColumnChip>
          ))}
        </HStack>
        <Text color="fg.muted" fontSize="sm">
          Columns marked with * are required.
        </Text>
        <HStack gap="3">
          <FileUpload.Root
            accept={["text/csv"]}
            onFileAccept={upload}
            width="auto"
          >
            <FileUpload.HiddenInput />
            <FileUpload.Trigger asChild>
              <Button loading={isUploading} variant="solid">
                <LuUpload size="16" /> Upload Deals
              </Button>
            </FileUpload.Trigger>
          </FileUpload.Root>
          <Button onClick={download} variant="ghost" textDecoration="underline">
            Download current file
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
}

function ColumnChip({ children }: { children: React.ReactNode }) {
  return (
    <Box
      background="bg.hover"
      borderRadius={"5px" as any}
      boxShadow={"inset 0 0 0 1px var(--chakra-colors-border-soft)" as any}
      color="fg"
      fontFamily="mono"
      fontSize={"12.5px" as any}
      lineHeight={"1" as any}
      paddingX={"7px" as any}
      paddingY={"5px" as any}
    >
      {children}
    </Box>
  );
}
